import type { OperationVariables, QueryResult } from '@apollo/client';

import type { GeneratedQueryHookType } from '@hrlogics/react-apollo-v3';

import { parseNullableValue } from '@clearverify/utils';

import {
  EmployerSimpleInfoFragment,
  EmployersSimpleListQuery,
  EmployersSimpleListQueryVariables,
  PeoSimpleInfoFragment,
  PeosSimpleListQuery,
  PeosSimpleListQueryVariables,
  QueryMode,
  useEmployersSimpleListQuery,
  usePeosSimpleListQuery,
} from 'src/api';

export const DashboardFilterCVAdminCustomFilterType = {
  Employer: 'Employer',
  PEO: 'PEO',
} as const;
export type DashboardFilterCVAdminCustomFilterType =
  (typeof DashboardFilterCVAdminCustomFilterType)[keyof typeof DashboardFilterCVAdminCustomFilterType];

export const DashboardFilterCVAdminCustomFormFields = {
  filters: 'filters',
} as const;

export type DashboardAutocompleteProps<TItem, TQuery, TVariables extends OperationVariables> = {
  useQuery: GeneratedQueryHookType<TQuery, TVariables>;
  getQueryItems: (data: QueryResult<TQuery, TVariables>['data']) => TItem[];
  queryFilter: (searchText: string) => TVariables;
  getOptionLabel: (option: TItem) => string;
};

export const DASHBOARD_CV_ADMIN_CUSTOM_FILTER_AUTOCOMPLETE_PROPS = {
  [DashboardFilterCVAdminCustomFilterType.Employer]: {
    useQuery: useEmployersSimpleListQuery,
    getQueryItems: data => data?.employerList || [],
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
    getOptionLabel: option => parseNullableValue(option.displayName),
  } satisfies DashboardAutocompleteProps<
    EmployerSimpleInfoFragment,
    EmployersSimpleListQuery,
    EmployersSimpleListQueryVariables
  >,

  [DashboardFilterCVAdminCustomFilterType.PEO]: {
    useQuery: usePeosSimpleListQuery,
    getQueryItems: data => data?.peoList || [],
    queryFilter: searchText => ({
      filter: { displayName: { contains: searchText, mode: QueryMode.Insensitive } },
    }),
    getOptionLabel: option => parseNullableValue(option.displayName),
  } satisfies DashboardAutocompleteProps<
    PeoSimpleInfoFragment,
    PeosSimpleListQuery,
    PeosSimpleListQueryVariables
  >,

  default: {
    useQuery: (() => ({})) as GeneratedQueryHookType,
    getQueryItems: () => [],
    queryFilter: () => ({}),
    getOptionLabel: () => '',
  } satisfies DashboardAutocompleteProps<unknown, unknown, object>,
} as const;

export const DASHBOARD_FILTER_CV_ADMIN_POPOVER_ANCHOR_GAP = 2.5;
